import { ISaSmsBilling, ISaSmsFeeItem } from '@interfaces/sa-sms';
import { convertPrice } from '@utils/resource';
import {
    Alert,
    Button,
    Card,
    Descriptions,
    Form,
    Input,
    InputNumber,
    notification,
    Space,
    Switch,
} from 'antd';
import { useSaSmsWrite } from 'hooks/use-sa-sms-api';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SaSmsReasonModal } from './sa-sms-reason-modal';
import { SmsFeeItemsTable } from './sms-fee-items-table';

type Props = { billing: ISaSmsBilling; canEdit: boolean; onChanged: () => void };
type Target =
    | { kind: 'price' }
    | { kind: 'grace' }
    | { kind: 'fee'; item: ISaSmsFeeItem; verifyOnly?: boolean };
type Values = {
    priceUsd?: number;
    graceDays?: number;
    amountUsd?: number;
    label?: string;
    active?: boolean;
    verified?: boolean;
};

/** Number price, grace days before auto-release, and the Twilio A2P fee items (saving or "mark verified" stamps verified_at). */
export const SmsBillingSettingsForm: React.FC<Props> = ({ billing, canEdit, onChanged }) => {
    const { t } = useTranslation(['sa-sms']);
    const { send } = useSaSmsWrite();
    const [target, setTarget] = useState<Target | null>(null);

    const confirm = async (v: Values & { reason: string }) => {
        if (!target) return;
        if (target.kind === 'price')
            await send('put', '/settings/billing/number-price', {
                price: Math.round((v.priceUsd ?? 0) * 100),
                reason: v.reason,
            });
        else if (target.kind === 'grace')
            await send('put', '/settings/billing/grace-days', {
                numberGraceDays: v.graceDays,
                reason: v.reason,
            });
        else {
            const body = target.verifyOnly
                ? { verified: true, reason: v.reason }
                : {
                      amount: Math.round((v.amountUsd ?? 0) * 100),
                      label: v.label?.trim(),
                      // The monthly campaign fee has no on/off switch (the API refuses it).
                      ...(target.item.feeType === 'monthly' ? {} : { active: v.active }),
                      verified: !!v.verified,
                      reason: v.reason,
                  };
            await send('put', `/settings/billing/fee-items/${target.item.code}`, body);
        }
        notification.success({ message: t('saved') });
        setTarget(null);
        onChanged();
    };

    const initial: Values =
        target?.kind === 'price'
            ? { priceUsd: billing.numberPrice / 100 }
            : target?.kind === 'grace'
            ? { graceDays: billing.numberGraceDays }
            : target?.kind === 'fee'
            ? {
                  amountUsd: target.item.amount / 100,
                  label: target.item.label,
                  active: target.item.active,
                  verified: false,
              }
            : {};

    return (
        <Card title={t('settings.numberBilling')}>
            <Descriptions column={1} size="small" bordered style={{ marginBottom: 16 }}>
                <Descriptions.Item label={t('settings.numberPrice')}>
                    <Space>
                        {convertPrice(billing.numberPrice)}
                        {canEdit && (
                            <Button size="small" onClick={() => setTarget({ kind: 'price' })}>
                                {t('actions.edit')}
                            </Button>
                        )}
                    </Space>
                </Descriptions.Item>
                <Descriptions.Item label={t('settings.graceDays')}>
                    <Space>
                        {billing.numberGraceDays}
                        {canEdit && (
                            <Button size="small" onClick={() => setTarget({ kind: 'grace' })}>
                                {t('actions.edit')}
                            </Button>
                        )}
                    </Space>
                </Descriptions.Item>
                <Descriptions.Item label={t('settings.campaignPrice')}>
                    {convertPrice(billing.campaignPrice)}
                </Descriptions.Item>
            </Descriptions>
            <Alert
                type="info"
                showIcon
                style={{ marginBottom: 12 }}
                message={t('settings.feePlansNote')}
                description={t('settings.authPlusNote')}
            />
            <SmsFeeItemsTable
                items={billing.feeItems}
                canEdit={canEdit}
                onEdit={(item) => setTarget({ kind: 'fee', item })}
                onVerify={(item) => setTarget({ kind: 'fee', item, verifyOnly: true })}
            />
            <SaSmsReasonModal<Values>
                open={!!target}
                title={target ? t(`settings.billingTitle.${target.kind}`) : ''}
                warning={target?.kind === 'price' ? t('settings.priceWarning') : undefined}
                initialValues={initial}
                onCancel={() => setTarget(null)}
                onConfirm={confirm}
            >
                {target?.kind === 'price' && (
                    <Form.Item
                        name="priceUsd"
                        label={t('settings.priceUsd')}
                        rules={[{ required: true }]}
                    >
                        <InputNumber
                            min={0}
                            max={100000}
                            precision={2}
                            prefix="$"
                            style={{ width: '100%' }}
                        />
                    </Form.Item>
                )}
                {target?.kind === 'grace' && (
                    <Form.Item
                        name="graceDays"
                        label={t('settings.graceDays')}
                        rules={[{ required: true }]}
                    >
                        <InputNumber min={1} max={90} precision={0} style={{ width: '100%' }} />
                    </Form.Item>
                )}
                {target?.kind === 'fee' && !target.verifyOnly && (
                    <>
                        <Form.Item
                            name="amountUsd"
                            label={t('settings.priceUsd')}
                            rules={[{ required: true }]}
                        >
                            <InputNumber
                                min={0}
                                max={100000}
                                precision={2}
                                prefix="$"
                                style={{ width: '100%' }}
                            />
                        </Form.Item>
                        <Form.Item
                            name="label"
                            label={t('settings.feeLabel')}
                            rules={[{ required: true, min: 3, max: 120 }]}
                        >
                            <Input maxLength={120} />
                        </Form.Item>
                        {target.item.feeType !== 'monthly' && (
                            <Form.Item
                                name="active"
                                label={t('settings.active')}
                                valuePropName="checked"
                            >
                                <Switch />
                            </Form.Item>
                        )}
                        <Form.Item
                            name="verified"
                            label={t('settings.verifyNow')}
                            valuePropName="checked"
                            extra={t('settings.verifyHelp')}
                        >
                            <Switch />
                        </Form.Item>
                    </>
                )}
            </SaSmsReasonModal>
        </Card>
    );
};
